import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useLogout } from '@biasafe.queries/auth-logout/auth-logout';
import { AuthHeading, AuthShell } from '../components/AuthShell';
import { MfaEnrollment } from '@/features/UserProfile/components/MfaEnrollment';
import { useUserStore } from '@/entities/user/store';
import { fetchMfaStatus, mfaIsDeferred } from '@/features/Auth/gcp/emailMfa';
import { returnToFromSearch } from '@/features/Auth/mfaReturn';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * Mandatory two-factor setup, shown after sign-in until a method is enrolled.
 *
 * Sits OUTSIDE the `_auth` route on purpose: that route's guard is what sends
 * people here, so hosting this page inside it would be a redirect loop.
 *
 * There is deliberately no "skip" and no "remind me later". FINRA/SEC treat
 * MFA on systems handling customer accounts as a control, not a preference —
 * an escape hatch here would make the control advisory, which is the same as
 * not having it.
 *
 * Uses the shared `AuthShell`. This screen carried its own copy that centred
 * the COLUMN but left everything inside it flush left, on a top-anchored
 * panel, under a 28px sans heading — so arriving here from /login was a visible
 * change of design language mid-flow. The shell centres vertically and
 * horizontally and supplies the 42px serif heading the earlier screens use.
 */
export function SecuritySetupPage() {
  const clearSession = useUserStore((state) => state.logout);
  // The heading says whatever the current step needs said, and nothing more.
  //
  // While choosing, that is the reason plus "choose a method". Once a method is
  // chosen it is the instruction for THAT step — the address the code went to,
  // how long it lasts — supplied by MfaEnrollment, which is where those facts
  // live. It used to be a generic subtitle, then a status line under it
  // repeating the same event, then a field label repeating the same
  // instruction: three blocks to say "type the code we emailed you".
  const [prompt, setPrompt] = useState<Array<string> | null>(null);
  const logoutMutation = useLogout();

  // Where the user was when the API asked them to enrol (set by the API client
  // on a 403 AU403MFAA), validated as a same-origin path. Enrolment returns
  // them there instead of the dashboard.
  const returnTo = returnToFromSearch(window.location.search);
  const { data: mfaStatus } = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });
  // Only where the environment defers enrolment to the first regulated action.
  // In the default mode this is a mandatory gate with no skip, by design (see
  // the component comment above), so the link does not exist there.
  const canDefer = !!mfaStatus && mfaIsDeferred(mfaStatus);

  // Nothing to set up for an account the backend says is already protected.
  // This page is the mandatory gate for people with NO second factor; being
  // shown it anyway (a stale tab, a bookmark, a refusal that raced an
  // enrolment) asks them to add a method they have, and Identity Platform then
  // answers "maximum number of sign-in methods". `degraded` is excluded: that
  // answer says "enrolled" only because the provider could not be read.
  const alreadyProtected =
    !!mfaStatus && mfaStatus.enrolled && !mfaStatus.degraded;
  useEffect(() => {
    if (alreadyProtected) window.location.assign(returnTo ?? '/dashboard');
  }, [alreadyProtected, returnTo]);

  /**
   * Actually sign out, rather than navigate to /login.
   *
   * This button used to `navigate({ to: '/login' })` and nothing else — which
   * TRAPPED the user. Still authenticated, LoginPage immediately redirects
   * them back in, the gate finds no second factor, and sends them straight
   * here again. A loop with no exit, on the one screen a stuck user reaches
   * for.
   *
   * Clear the server session first, then the client one, then hard-navigate
   * so nothing rehydrates from memory. The local clear happens on failure too:
   * being unable to reach /auth/logout must not mean being unable to leave.
   */
  const signOut = () => {
    const finish = () => {
      clearSession();
      window.location.href = '/login';
    };
    logoutMutation.mutate({ data: undefined } as never, {
      onSuccess: finish,
      onError: finish,
    });
  };

  return (
    <AuthShell dataName="SecuritySetup">
      <AuthHeading
        title="Secure your account"
        eyebrow={
          <div className="flex items-center justify-center gap-2 text-[#7F22FE]">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-[13px] font-medium uppercase tracking-wide">
              Required
            </span>
          </div>
        }
      >
        {/* Still says WHY, not just that it is mandatory — people comply with a
            reason and resent an unexplained wall — but only while that is the
            question. Once a method is picked the step's own instruction takes
            this slot. */}
        {prompt
          ? // One sentence per line, broken here rather than left to the
            // width: the instruction should never be the tail of a wrap.
            prompt.map((line, i) => (
              <span key={line}>
                {i > 0 && <br />}
                {line}
              </span>
            ))
          : 'To keep your account and data secure, we require a two-step sign-in. Choose your verification method.'}
      </AuthHeading>

      <div className="w-full">
        <MfaEnrollment
          required
          onPromptChange={setPrompt}
          onEnrolled={() => {
            // Hard navigation: the gate that sent us here reads the
            // backend's answer, and a client-side push would race the
            // cache it just invalidated.
            window.location.assign(returnTo ?? '/dashboard');
          }}
        />
      </div>

      {/* A mandatory gate with no way out is a lockout waiting to happen. Both
          methods depend on delivery we do not fully control — SMS on the
          provider being enabled, email on the receiving server accepting our
          mail — so if neither works the user must be able to reach a human
          rather than stare at a wall.

          Only while choosing, though. "Neither method working?" is a question
          about the choice, and once one is made the screen is about the code
          that was just sent — the offer stops matching what is on screen and
          becomes noise under the thing the user is trying to do. Backing out
          brings it back.

          "Sign out instead" below is NOT gated: it is the only exit from a
          mandatory gate, and it has to be reachable from every step. */}
      {!prompt && (
        <p className="w-full text-center text-[13px] leading-[1.6] text-[#717182]">
          Neither method working? That is a problem on our end.
          {/* Broken deliberately, not left to wrap. The first line is the
            reassurance and the second is the action, and a wrap point chosen by
            the viewport would split them anywhere. */}
          <br />
          Email{' '}
          <a
            className="underline"
            href="mailto:support@biasafe.ai?subject=Cannot%20set%20up%20two-factor%20authentication"
          >
            support@biasafe.ai
          </a>{' '}
          and we will sort it out.
        </p>
      )}

      {canDefer && !prompt && (
        <a
          href={returnTo ?? '/dashboard'}
          className="text-[14px] text-[#121212] hover:underline"
        >
          Not now: regulated actions stay locked until you finish
        </a>
      )}

      <button
        type="button"
        onClick={signOut}
        // Same treatment as "Resend email" on the confirm-your-email screen:
        // bold black, 14px. It is the one way off this gate, and grey read as
        // disabled next to two live method cards.
        className="text-[14px] text-[#121212] font-bold hover:underline"
      >
        Sign out instead
      </button>
    </AuthShell>
  );
}
